import React, { useEffect, useRef, useState } from "react";
import { Check, X, UserMinus, UsersRound } from "lucide-react";

import ViewContainer from "../../../../../components/layout/view/ViewContainer";
import ContentPanel from "../../../../../components/layout/content/ContentPanel";
import ContentPanelHeader from "../../../../../components/layout/content/ContentPanelHeader";
import ContentItem from "../../../../../components/layout/content/ContentItem";

import SecondaryPillTabs from "../../../../../components/tabs/SecondaryPillTabs";
import EmptyState from "../../../../../components/feedback/EmptyState";
import { EllipsisMenu } from "../../../../../components/menu/ellipsis/EllipsisMenu";

import { useCreatorGroups } from "../../hooks/useCreatorGroups";

import "./GroupCreatorMembers.css";

const getErrorMessage = (error) =>
  typeof error === "string"
    ? error
    : error?.response?.data?.message || error?.message || "Request failed.";

const getStatus = (member) =>
  String(member?.status || "")
    .trim()
    .toLowerCase();

const getUsername = (member) =>
  String(member?.username || member?.fullName || "").trim() ||
  `User ${member?.userId || ""}`;

export default function GroupCreatorMembers({ username, groupTag }) {
  const {
    members,
    memberCounts,
    membersLoading,
    membersError,
    getCreatorGroupMembers,
    updateCreatorGroupMemberStatus,
    removeCreatorGroupMember,
  } = useCreatorGroups(username);

  const fetchRef = useRef(getCreatorGroupMembers);
  fetchRef.current = getCreatorGroupMembers;

  const [statusFilter, setStatusFilter] = useState("all");
  const [workingMemberId, setWorkingMemberId] = useState(null);
  const [actionError, setActionError] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;

    setStatusFilter("all");
    setActionError("");
    setLoaded(false);

    if (!username || !groupTag) {
      setLoaded(true);
      return;
    }

    const loadMembers = async () => {
      try {
        await fetchRef.current(groupTag, "all");
      } catch {
        // Errors are handled by the hook.
      } finally {
        if (active) setLoaded(true);
      }
    };

    void loadMembers();

    return () => {
      active = false;
    };
  }, [username, groupTag]);

  const list = Array.isArray(members) ? members : [];

  const counts = {
    total: Number(memberCounts?.total ?? list.length),
    approved: Number(memberCounts?.approved ?? 0),
    pending: Number(memberCounts?.pending ?? 0),
  };

  const tabs = [
    { id: "all", label: `All (${counts.total})` },
    { id: "approved", label: `Approved (${counts.approved})` },
    { id: "pending", label: `Pending (${counts.pending})` },
  ];

  const filteredMembers =
    statusFilter === "all"
      ? list
      : list.filter((member) => getStatus(member) === statusFilter);

  const handleTabChange = (selected) => {
    const tab =
      typeof selected === "string" ? selected : selected?.id || selected?.value;

    if (tabs.some((item) => item.id === tab)) {
      setStatusFilter(tab);
    }
  };

  const handleChangeStatus = async (memberId, status) => {
    if (workingMemberId !== null) return;

    const action = status === "Approved" ? "Approve" : "Decline";

    if (!window.confirm(`${action} this membership request?`)) {
      return;
    }

    setWorkingMemberId(memberId);
    setActionError("");

    try {
      await updateCreatorGroupMemberStatus(groupTag, memberId, status);
    } catch (error) {
      setActionError(getErrorMessage(error));
    } finally {
      setWorkingMemberId(null);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (workingMemberId !== null) return;

    if (!window.confirm("Remove this member from the group?")) {
      return;
    }

    setWorkingMemberId(memberId);
    setActionError("");

    try {
      await removeCreatorGroupMember(groupTag, memberId);
    } catch (error) {
      setActionError(getErrorMessage(error));
    } finally {
      setWorkingMemberId(null);
    }
  };

  return (
    <ViewContainer>
      <ContentPanel>
        <ContentPanelHeader
          title={`Group Members (${counts.approved})`}
          subTitle={`${counts.pending} pending request${
            counts.pending === 1 ? "" : "s"
          }. Review requests and manage members.`}
        />

        <SecondaryPillTabs
          tabs={tabs}
          activeTab={statusFilter}
          onChange={handleTabChange}
        />

        <br />

        {(actionError || membersError) && (
          <p className="creator-owner-group-members__error" role="alert">
            {actionError || getErrorMessage(membersError)}
          </p>
        )}

        {!loaded || (membersLoading && !list.length) ? (
          <p className="creator-owner-group-members__loading" role="status">
            Loading members...
          </p>
        ) : filteredMembers.length === 0 ? (
          <EmptyState
            icon={<UsersRound size={28} />}
            title={
              statusFilter === "pending"
                ? "No pending requests"
                : statusFilter === "approved"
                  ? "No approved members"
                  : "No members found"
            }
            description={
              statusFilter === "pending"
                ? "New membership requests will appear here."
                : statusFilter === "approved"
                  ? "Approved group members will appear here."
                  : "Members who request access will appear here."
            }
          />
        ) : (
          <div className="creator-owner-group-members__list">
            {filteredMembers.map((member) => {
              const memberId = Number(member.memberId);
              const status = getStatus(member);
              const name = getUsername(member);

              const options = [
                ...(status === "pending"
                  ? [
                      {
                        label: "Approve Request",
                        action: () => handleChangeStatus(memberId, "Approved"),
                        disabled: workingMemberId !== null,
                      },
                      {
                        label: "Decline Request",
                        action: () => handleChangeStatus(memberId, "Declined"),
                        disabled: workingMemberId !== null,
                      },
                    ]
                  : []),
                {
                  label: "Remove Member",
                  action: () => handleRemoveMember(memberId),
                  variant: "danger",
                  disabled: workingMemberId !== null,
                },
              ];

              return (
                <ContentItem
                  key={member.memberId}
                  className="creator-owner-group-members__item"
                  actions={
                    <EllipsisMenu
                      position="right"
                      disabled={workingMemberId !== null}
                      options={options}
                    />
                  }
                >
                  <div style={{ display: "flex", gap: "1rem" }}>
                    <div className="creator-owner-group-members__avatar">
                      {name.charAt(0).toUpperCase()}
                    </div>

                    <div className="creator-owner-group-members__info">
                      <div className="creator-owner-group-members__identity">
                        <strong className="creator-owner-group-members__name">
                          {member.fullName || name}
                        </strong>

                        <span
                          className={`creator-owner-group-members__status creator-owner-group-members__status--${status}`}
                        >
                          {status}
                        </span>
                      </div>

                      <p className="creator-owner-group-members__reason">
                        {member.reasonForJoining || "No reason provided."}
                      </p>
                    </div>
                  </div>
                </ContentItem>
              );
            })}
          </div>
        )}
      </ContentPanel>
    </ViewContainer>
  );
}
